import type { Article } from '../types'

export const site = {
  name: 'Aquatic Contractors',
  origin: 'https://aquatic-contractors.com',
  apiOrigin: 'https://api.aquatic-contractors.com',
  phoneDisplay: '(805) 660-0924',
  phoneHref: 'tel:+18056600924',
  founder: 'Brian Hoover',
  serviceRegion: 'Southern California — Santa Barbara to San Diego',
}

export const services = [
  {
    id: 'design-installation',
    number: '01',
    title: 'Custom aquarium design & installation',
    shortTitle: 'Design & installation',
    summary:
      'A complete system designed around your room, your animals, and the way you want to live with it.',
    details: [
      'Residential and commercial aquarium planning',
      'Freshwater, saltwater, reef, and specialty systems',
      'Custom filtration, plumbing, lighting, and cabinetry coordination',
      'Installation, stocking guidance, and system handoff',
    ],
  },
  {
    id: 'maintenance',
    number: '02',
    title: 'Aquarium maintenance & service',
    shortTitle: 'Maintenance & service',
    summary:
      'Consistent, expert care that keeps high-end aquariums, ponds, and aquaponic systems healthy and presentation-ready.',
    details: [
      'Routine cleaning and water testing',
      'Equipment inspection and preventative maintenance',
      'Coral, plant, fish, and invertebrate care',
      'Pond, water-feature, and aquaponic system service',
    ],
  },
  {
    id: 'emergency-repair',
    number: '03',
    title: 'Emergency aquarium repair',
    shortTitle: 'Emergency repair',
    summary:
      'Fast diagnosis when leaks, plumbing, filtration, water quality, or livestock health cannot wait.',
    details: [
      'Leak and tank-seal diagnosis',
      'Filter, pump, plumbing, and equipment repair',
      'Fish and coral health troubleshooting',
      'Same-day service when scheduling and location allow',
    ],
  },
  {
    id: 'livestock',
    number: '04',
    title: 'Fish, coral & invertebrate sourcing',
    shortTitle: 'Livestock sourcing',
    summary:
      'Thoughtful sourcing and placement of compatible fish, plants, coral, and invertebrates from around the world.',
    details: [
      'Species and compatibility guidance',
      'Freshwater and marine livestock sourcing',
      'Coral and aquatic plant selection',
      'Acclimation and long-term care planning',
    ],
  },
]

export const serviceAreas = [
  'Malibu',
  'Westlake Village',
  'Lake Sherwood',
  'Ventura',
  'Camarillo',
  'Thousand Oaks',
  'Moorpark',
  'Simi Valley',
  'Agoura Hills',
  'Calabasas',
  'Hidden Hills',
  'Bell Canyon',
  'Topanga Canyon',
  'Encino',
  'Hollywood',
  'Santa Monica',
  'Beverly Hills',
  'Newbury Park',
  'Woodland Hills',
  'Sherman Oaks',
  'Studio City',
  'Pasadena',
]

export const projects = [
  {
    image: '/images/optimized/hero.webp',
    alt: 'Colorful reef aquarium maintained by Aquatic Contractors',
    title: 'Living reef systems',
    category: 'Reef · Maintenance',
  },
  {
    image: '/images/optimized/restaurant.webp',
    alt: 'Large cylindrical custom aquarium in a restaurant interior',
    title: 'Hospitality centerpieces',
    category: 'Commercial · Custom fabrication',
  },
  {
    image: '/images/optimized/aquarium-wall.webp',
    alt: 'Two built-in illuminated aquariums in a residential media wall',
    title: 'Architectural installations',
    category: 'Residential · Design & installation',
  },
]

export const notableClients = [
  {
    name: 'Howie Mandel',
    image: '/images/optimized/client-howie.webp',
    alt: 'Aquatic Contractors owner Brian Hoover with client Howie Mandel',
  },
  {
    name: 'Tyga',
    image: '/images/optimized/client-tyga.webp',
    alt: 'Aquatic Contractors owner Brian Hoover with client Tyga',
  },
  {
    name: 'Johnny Damon',
    image: '/images/optimized/client-johnny-damon.webp',
    alt: 'Aquatic Contractors owner Brian Hoover with Johnny Damon',
  },
  {
    name: 'Redfoo',
    image: '/images/optimized/client-redfoo.webp',
    alt: 'Aquatic Contractors owner Brian Hoover with client Redfoo',
  },
]

export const testimonials = [
  'Brian is amazing at his trade. He is really mastering the aquarium industry and is showing why he is rising above the rest. Our aquarium transformed over the course of a few weeks from an average aquarium into something I am amazed with.',
  'Great service. Brian came in and pointed out all the flaws the previous service company installed and did not install properly. He spent three hours fixing the problems and only charged me for the parts on that first service. He proved why he is the best.',
  'Aquatic Contractors saved the day. My previous service company gave me the runaround about why my tank would not thrive. Aquatic Contractors had my fish living and coral growing in under a month. Brian is always on time and takes great pride in his work.',
]

export const articles: Article[] = [
  {
    slug: 'los-angeles-emergency-aquarium-help',
    title: 'Los Angeles emergency aquarium help',
    eyebrow: 'Emergency service',
    description:
      'What to do when an aquarium leak, plumbing problem, failed filter, or livestock health issue needs immediate expert attention.',
    published: '2019-01-31T09:54:12-08:00',
    modified: '2023-03-19T17:48:15-07:00',
    readTime: '3 min read',
    image: '/images/optimized/contact-page.webp',
    imageAlt: 'Detailed coral reef aquarium requiring expert care',
    sections: [
      {
        paragraphs: [
          'When something goes wrong with your aquarium, filter, or plumbing, the situation can become stressful quickly. With more than 50 years of combined experience, the Aquatic Contractors team knows how to diagnose and fix aquarium problems throughout Los Angeles and the surrounding region.',
          'We regularly see systems that were installed incorrectly by another company, along with small issues that grew into urgent problems over time. Many problems can be diagnosed with you over the phone right away. For true emergencies, same-day service may be available depending on location and scheduling.',
        ],
      },
      {
        heading: 'Emergency aquarium services',
        list: [
          'Diagnosing aquarium problems',
          'Finding and fixing leaks',
          'Sealing a leaking fish tank',
          'Repairing aquarium plumbing',
          'Troubleshooting fish and coral health',
          'Repairing broken filters, pumps, and related equipment',
        ],
      },
      {
        heading: 'Start with a phone call',
        paragraphs: [
          'If water is near an electrical source, do not touch the water or equipment. Move people and pets away, shut off power only if you can do so safely, and call an appropriate emergency service if there is an immediate electrical or structural hazard.',
          'For aquarium-specific help, call Aquatic Contractors and describe what you see. Photos of the tank, filtration, plumbing, and affected area can help us assess the next step.',
        ],
      },
    ],
  },
  {
    slug: 'aquatic-contractors-featured-in-the-thousand-oaks-acorn-newspaper',
    title: 'Aquatic Contractors featured in the Thousand Oaks Acorn',
    eyebrow: 'In the press',
    description:
      'The Thousand Oaks Acorn profiled Aquatic Contractors founder Brian Hoover and his personalized aquarium work across Southern California.',
    published: '2019-01-31T10:11:09-08:00',
    modified: '2020-05-29T13:12:03-07:00',
    readTime: '2 min read',
    image: '/images/optimized/client-howie.webp',
    imageAlt: 'Aquatic Contractors founder Brian Hoover with Howie Mandel',
    sections: [
      {
        paragraphs: [
          'Aquatic Contractors owner Brian Hoover was raised in Thousand Oaks, California, and has provided high-caliber aquatic service across Santa Barbara, Ventura, Los Angeles, Orange County, and San Diego for more than 20 years.',
          'The Thousand Oaks Acorn featured Brian’s path into the aquarium industry and his work creating personalized aquatic environments for homes, businesses, and well-known clients.',
        ],
        externalLink: {
          href: 'https://www.toacorn.com/articles/t-o-native-makes-splash-with-personalized-aquariums/',
          label: 'Read “T.O. native makes splash with personalized aquariums” in the Thousand Oaks Acorn',
        },
      },
    ],
  },
  {
    slug: 'ventura-county-custom-aquariums',
    title: 'Ventura County custom aquariums',
    eyebrow: 'Design & installation',
    description:
      'Plan a custom home or office aquarium correctly from the start with experienced design, filtration, plumbing, and installation guidance.',
    published: '2019-01-31T11:13:19-08:00',
    modified: '2020-05-29T13:11:49-07:00',
    readTime: '3 min read',
    image: '/images/optimized/aquarium-wall.webp',
    imageAlt: 'Custom built-in aquariums in a Southern California home',
    sections: [
      {
        paragraphs: [
          'When you add an aquarium to your home or office, getting the design and build right the first time can prevent expensive rework and years of maintenance frustration. Many customers come to us after another company installed a system that did not fit the space, the livestock, or the realities of ongoing care.',
          'Aquatic Contractors helps shape the aquarium, filtration, plumbing, lighting, access, and service plan as one complete system. The result is easier to maintain, quieter to live with, and designed for the animals it will support.',
        ],
      },
      {
        heading: 'Areas we serve',
        paragraphs: [
          'Our custom aquarium work spans Ventura County, Los Angeles County, and communities throughout Southern California, including:',
        ],
        list: serviceAreas.slice(0, 16),
      },
      {
        heading: 'Plan before construction begins',
        paragraphs: [
          'The best time to involve an aquarium specialist is before cabinetry, electrical, plumbing, and structural decisions are final. We can also assess an existing room or system and recommend the most practical path forward.',
        ],
      },
    ],
  },
]

export const articleRoutes = articles.map((article) => `/${article.slug}/`)

export const staticRoutes = ['/', '/services/', '/contact/', '/insights/', ...articleRoutes]
